<template>
  <div class="sublet-dash">
    <div v-if="isLoading" class="sublet-loading">
      <BaseLoadingSpinner :with-overlay="false" :size="60" />
    </div>
    <div v-else-if="hasError" class="sublet-error">
      <p>Kunde inte ladda andrahandsstatistik</p>
    </div>
    <template v-else>
      <div class="example-data-banner">
        &#9888; Exempeldata &mdash; visas med testdata
      </div>
      <div class="sublet-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="sublet-tab"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
        <div class="sublet-summary">
          <span class="summary-badge critical">
            {{ criticalCount }} Kritiska
          </span>
          <span class="summary-badge overcharge">
            {{ overchargeCount }} Överhyra
          </span>
          <span class="summary-badge active-count">
            {{ activeCount }} Aktiva
          </span>
        </div>
      </div>

      <!-- RISKFLAGGOR -->
      <div v-if="activeTab === 'risk'" class="tab-content">
        <div class="sort-bar">
          <span class="sort-label">Sortera:</span>
          <button
            v-for="s in sortOptions"
            :key="s.id"
            class="sort-btn"
            :class="{ active: sortBy === s.id }"
            @click="sortBy = s.id"
          >
            {{ s.label }}
          </button>
        </div>
        <table class="risk-table">
          <thead>
            <tr>
              <th>Ärende-ID</th>
              <th>Adress</th>
              <th>Risk</th>
              <th>Förstahandshyra</th>
              <th>Begärd a-handshyra</th>
              <th>Skäl</th>
              <th>Flaggor</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="app in sortedRiskApps" :key="app.id">
              <td class="id-cell">AH-{{ app.id }}</td>
              <td>{{ app.address }}, {{ app.city }}</td>
              <td>
                <span
                  class="risk-badge"
                  :class="riskLevel(app.riskScore).class"
                >
                  {{ app.riskScore }}
                </span>
              </td>
              <td>
                {{ app.rent ? formatNum(app.rent) + ' kr' : '\u2014' }}
              </td>
              <td>
                <template v-if="Number(app.rentTo) > 0 && Number(app.rent) > 0">
                  {{ formatNum(app.rentTo) }} kr
                  <span
                    v-if="overchargePercent(app) > 0"
                    class="overcharge-pct"
                    :class="{
                      high: overchargePercent(app) > 30,
                      medium: overchargePercent(app) <= 30
                    }"
                  >
                    +{{ overchargePercent(app) }}%
                  </span>
                </template>
                <template v-else>{{
                  Number(app.rentTo) > 0
                    ? formatNum(app.rentTo) + ' kr'
                    : '\u2014'
                }}</template>
              </td>
              <td>{{ reasonLabel(app.reasonType) }}</td>
              <td>
                <div class="flags">
                  <span
                    v-for="flag in getFlags(app)"
                    :key="flag.text"
                    class="flag-badge"
                    :class="flag.type"
                  >
                    {{ flag.text }}
                  </span>
                  <span v-if="!getFlags(app).length">&mdash;</span>
                </div>
              </td>
              <td>
                <span
                  class="status-badge"
                  :class="statusClass(app.landlordStatus)"
                >
                  {{ statusLabel(app.landlordStatus) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ÖVERHYRA -->
      <div v-if="activeTab === 'overcharge'" class="tab-content">
        <div class="stat-cards">
          <div class="stat-card">
            <div class="stat-card-label">Fall med överhyra</div>
            <div class="stat-card-value warning">
              {{ overchargeCount }} av {{ activeCount }}
            </div>
            <div class="stat-card-sub">
              {{ overchargePercOfActive }}% av aktiva avtal
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-card-label">Genomsnittlig överhyra</div>
            <div class="stat-card-value warning">
              +{{ avgOverchargePercent }}%
            </div>
            <div class="stat-card-sub">bland överträdare</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-label">Högsta uppmätta</div>
            <div class="stat-card-value danger">
              +{{ maxOverchargePercent }}%
            </div>
            <div class="stat-card-sub">{{ maxOverchargeAddress }}</div>
          </div>
        </div>

        <table class="risk-table">
          <thead>
            <tr>
              <th>Ärende-ID</th>
              <th>Adress</th>
              <th>Förstahandshyra</th>
              <th>Begärd a-handshyra</th>
              <th>Överhyra</th>
              <th>Skäl</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="app in overchargeApps" :key="app.id">
              <td class="id-cell">AH-{{ app.id }}</td>
              <td>{{ app.address }}, {{ app.city }}</td>
              <td>{{ formatNum(app.rent) }} kr</td>
              <td>{{ formatNum(app.rentTo) }} kr</td>
              <td>
                <span
                  class="overcharge-pct"
                  :class="{
                    high: overchargePercent(app) > 30,
                    medium: overchargePercent(app) <= 30
                  }"
                >
                  +{{ overchargePercent(app) }}%
                </span>
              </td>
              <td>{{ reasonLabel(app.reasonType) }}</td>
              <td>
                <span
                  class="status-badge"
                  :class="statusClass(app.landlordStatus)"
                >
                  {{ statusLabel(app.landlordStatus) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="scatter-section">
          <h4 class="section-heading">
            Förstahandshyra vs begärd andrahandshyra
          </h4>
          <p class="scatter-subtitle">
            Punkter ovanför diagonalen = överhyra. Storlek indikerar
            procentsats.
          </p>
          <div ref="scatterContainer" class="scatter-container">
            <svg ref="scatterSvg"></svg>
          </div>
          <div class="scatter-legend">
            <span class="scatter-legend-item">
              <span
                class="scatter-legend-dot"
                style="background: #2a9d8f"
              ></span>
              Laglig (0%)
            </span>
            <span class="scatter-legend-item">
              <span
                class="scatter-legend-dot"
                style="background: #c98a00"
              ></span>
              Marginell överhyra (&lt;30%)
            </span>
            <span class="scatter-legend-item">
              <span
                class="scatter-legend-dot"
                style="background: #e04545"
              ></span>
              Olaglig överhyra (30%+)
            </span>
          </div>
        </div>
      </div>

      <!-- ÅTERKOMMANDE ADRESSER -->
      <div v-if="activeTab === 'recurring'" class="tab-content">
        <div class="stat-cards">
          <div class="stat-card">
            <div class="stat-card-label">Adresser med 2+ ärenden</div>
            <div class="stat-card-value">
              {{ recurringAddresses.length }} st
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-card-label">Längsta mönstret</div>
            <div class="stat-card-value">{{ longestRecurring.months }} mån</div>
            <div class="stat-card-sub">{{ longestRecurring.address }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-label">Adresser med överhyra</div>
            <div class="stat-card-value">
              {{ recurringWithOvercharge }} av
              {{ recurringAddresses.length }}
            </div>
            <div class="stat-card-sub">Mönster = förhöjd risk</div>
          </div>
        </div>

        <h4 class="section-heading">
          Adresser med upprepade andrahandsärenden
        </h4>
        <div
          v-for="addr in recurringAddresses"
          :key="addr.address"
          class="recurring-row"
        >
          <div class="recurring-address">
            <strong>{{ addr.address }}</strong>
            <span class="recurring-city">{{ addr.city }}</span>
          </div>
          <div class="recurring-stat">
            <strong>{{ addr.applicationCount }}</strong>
            <span>ärenden</span>
          </div>
          <div class="recurring-stat">
            <strong>{{ addr.totalMonths || 0 }} mån</strong>
            <span>total andrahandstid</span>
          </div>
          <div class="recurring-stat">
            <strong
              :class="{
                'text-danger': addr.avgOverchargePercent > 30,
                'text-warning':
                  addr.avgOverchargePercent > 0 &&
                  addr.avgOverchargePercent <= 30
              }"
            >
              {{
                addr.avgOverchargePercent
                  ? '+' + Math.round(addr.avgOverchargePercent) + '%'
                  : 'Ingen'
              }}
            </strong>
            <span>genomsnittlig överhyra</span>
          </div>
        </div>
      </div>

      <!-- ÄRENDEFLÖDE -->
      <div v-if="activeTab === 'flow'" class="tab-content">
        <div class="flow-chart-section">
          <h4 class="section-heading">
            Månadsvis ärendeflöde &mdash; Inkomna, Godkända, Avslagna
          </h4>
          <div class="flow-chart">
            <div
              v-for="month in monthlyFlow"
              :key="month.month"
              class="flow-bar-group"
            >
              <div class="flow-bars">
                <div
                  class="flow-bar incoming"
                  :style="{ height: barHeight(month.incoming) + 'px' }"
                >
                  <span v-if="month.incoming" class="bar-val">{{
                    month.incoming
                  }}</span>
                </div>
                <div
                  class="flow-bar approved"
                  :style="{ height: barHeight(month.approved) + 'px' }"
                >
                  <span v-if="month.approved" class="bar-val">{{
                    month.approved
                  }}</span>
                </div>
                <div
                  class="flow-bar rejected"
                  :style="{ height: barHeight(month.rejected) + 'px' }"
                >
                  <span v-if="month.rejected" class="bar-val">{{
                    month.rejected
                  }}</span>
                </div>
              </div>
              <div class="flow-month-label">{{ monthLabel(month.month) }}</div>
            </div>
          </div>
          <div class="flow-legend">
            <span class="flow-legend-item">
              <span class="flow-legend-dot incoming"></span> Inkomna
            </span>
            <span class="flow-legend-item">
              <span class="flow-legend-dot approved"></span> Godkända
            </span>
            <span class="flow-legend-item">
              <span class="flow-legend-dot rejected"></span> Avslagna
            </span>
          </div>
        </div>

        <div class="stat-cards">
          <div class="stat-card">
            <div class="stat-card-label">Genomsnittlig handläggningstid</div>
            <div class="stat-card-value">{{ avgProcessingDays }} dagar</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-label">Vanligaste skäl</div>
            <div class="stat-card-value">{{ topReason.label }}</div>
            <div class="stat-card-sub">{{ topReason.count }} ärenden</div>
          </div>
          <div class="stat-card">
            <div class="stat-card-label">Godkännandegrad</div>
            <div class="stat-card-value">{{ approvalRate }}%</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import * as d3 from 'd3';
import BaseLoadingSpinner from '@/components/BaseLoadingSpinner.vue';

// TODO: Replace with real endpoint when available
// 12 ärenden: 10 aktiva (ej CLOSED/REJECTED/REMOVED), 3 med >15% överhyra
const MOCK_APPLICATIONS = [
  // >15% överhyra — flaggas
  {
    id: 1201,
    address: 'Sveavägen 42',
    city: 'Stockholm',
    rent: 8500,
    rentTo: 12500,
    reasonType: 'arbete',
    landlordStatus: 'APPROVED',
    startDate: '2025-06-01',
    endDate: '2026-06-01',
    sentToHostAt: '2025-05-10',
    createdAt: '2025-05-01'
  },
  {
    id: 1204,
    address: 'Birger Jarlsgatan 22',
    city: 'Stockholm',
    rent: 11000,
    rentTo: 15400,
    reasonType: 'arbete',
    landlordStatus: 'INCOMING',
    startDate: '2026-01-01',
    sentToHostAt: '2025-12-15',
    createdAt: '2025-12-01'
  },
  {
    id: 1209,
    address: 'Valhallavägen 90',
    city: 'Stockholm',
    rent: 12500,
    rentTo: 18000,
    reasonType: 'other',
    landlordStatus: 'READY',
    startDate: '2026-02-01',
    sentToHostAt: '2026-01-10',
    createdAt: '2026-01-05'
  },
  // Inom 15% — rimligt möbleringspåslag, ej överhyra
  {
    id: 1203,
    address: 'Odengatan 55',
    city: 'Stockholm',
    rent: 7800,
    rentTo: 8600,
    reasonType: 'sambo',
    landlordStatus: 'APPROVED',
    startDate: '2025-09-01',
    endDate: '2026-03-01',
    sentToHostAt: '2025-08-20',
    createdAt: '2025-08-10'
  },
  {
    id: 1208,
    address: 'Kungsholmsgatan 12',
    city: 'Stockholm',
    rent: 9800,
    rentTo: 10800,
    reasonType: 'sambo',
    landlordStatus: 'APPROVED',
    startDate: '2025-11-01',
    endDate: '2026-05-01',
    sentToHostAt: '2025-10-15',
    createdAt: '2025-10-01'
  },
  {
    id: 1210,
    address: 'Södermalmsallén 5',
    city: 'Stockholm',
    rent: 6800,
    rentTo: 7500,
    reasonType: 'studie',
    landlordStatus: 'APPROVED',
    startDate: '2025-08-15',
    endDate: '2026-06-15',
    sentToHostAt: '2025-07-25',
    createdAt: '2025-07-15'
  },
  // Samma hyra eller omöblerat
  {
    id: 1202,
    address: 'Kungsgatan 18',
    city: 'Stockholm',
    rent: 9200,
    rentTo: 9200,
    reasonType: 'studie',
    landlordStatus: 'APPROVED',
    startDate: '2025-08-01',
    endDate: '2026-08-01',
    sentToHostAt: '2025-07-15',
    createdAt: '2025-07-01'
  },
  {
    id: 1207,
    address: 'Götgatan 31',
    city: 'Stockholm',
    rent: 7500,
    rentTo: 7500,
    reasonType: 'arbete',
    landlordStatus: 'APPROVED',
    startDate: '2025-04-01',
    endDate: '2026-04-01',
    sentToHostAt: '2025-03-10',
    createdAt: '2025-03-01'
  },
  {
    id: 1212,
    address: 'Odengatan 55',
    city: 'Stockholm',
    rent: 7800,
    rentTo: 8000,
    reasonType: 'arbete',
    landlordStatus: 'APPROVED',
    startDate: '2024-09-01',
    endDate: '2025-09-01',
    sentToHostAt: '2024-08-15',
    createdAt: '2024-08-01'
  },
  {
    id: 1213,
    address: 'Norrtullsgatan 8',
    city: 'Stockholm',
    rent: 8200,
    rentTo: 8400,
    reasonType: 'studie',
    landlordStatus: 'APPROVED',
    startDate: '2025-10-01',
    endDate: '2026-06-01',
    sentToHostAt: '2025-09-15',
    createdAt: '2025-09-01'
  },
  // Avslutade / avslagna (ej aktiva)
  {
    id: 1206,
    address: 'Hornsgatan 88',
    city: 'Stockholm',
    rent: 6200,
    rentTo: 6500,
    reasonType: 'studie',
    landlordStatus: 'REJECTED',
    startDate: '2025-09-01',
    endDate: '2026-06-01',
    sentToHostAt: '2025-08-05',
    createdAt: '2025-07-20'
  },
  {
    id: 1211,
    address: 'Fleminggatan 44',
    city: 'Stockholm',
    rent: 8900,
    rentTo: 9100,
    reasonType: 'other',
    landlordStatus: 'CLOSED',
    startDate: '2024-06-01',
    endDate: '2025-06-01',
    sentToHostAt: '2024-05-10',
    createdAt: '2024-05-01'
  },
  // Extra entries for scatter plot density
  {
    id: 1214,
    address: 'Drottninggatan 60',
    city: 'Stockholm',
    rent: 7200,
    rentTo: 8100,
    reasonType: 'studie',
    landlordStatus: 'APPROVED',
    startDate: '2025-05-01',
    endDate: '2026-05-01',
    sentToHostAt: '2025-04-10',
    createdAt: '2025-04-01'
  },
  {
    id: 1215,
    address: 'Hantverkargatan 25',
    city: 'Stockholm',
    rent: 10500,
    rentTo: 10800,
    reasonType: 'arbete',
    landlordStatus: 'APPROVED',
    startDate: '2025-07-01',
    endDate: '2026-07-01',
    sentToHostAt: '2025-06-15',
    createdAt: '2025-06-01'
  },
  {
    id: 1216,
    address: 'Ringvägen 100',
    city: 'Stockholm',
    rent: 6500,
    rentTo: 9200,
    reasonType: 'other',
    landlordStatus: 'APPROVED',
    startDate: '2025-10-01',
    endDate: '2026-04-01',
    sentToHostAt: '2025-09-10',
    createdAt: '2025-09-01'
  },
  {
    id: 1217,
    address: 'Scheelegatan 15',
    city: 'Stockholm',
    rent: 8800,
    rentTo: 9000,
    reasonType: 'sambo',
    landlordStatus: 'APPROVED',
    startDate: '2025-06-15',
    endDate: '2026-06-15',
    sentToHostAt: '2025-05-20',
    createdAt: '2025-05-10'
  },
  {
    id: 1218,
    address: 'Tavastgatan 30',
    city: 'Stockholm',
    rent: 5800,
    rentTo: 5800,
    reasonType: 'studie',
    landlordStatus: 'APPROVED',
    startDate: '2025-08-01',
    endDate: '2026-06-01',
    sentToHostAt: '2025-07-10',
    createdAt: '2025-07-01'
  },
  {
    id: 1219,
    address: 'Pontonjärgatan 40',
    city: 'Stockholm',
    rent: 11200,
    rentTo: 14500,
    reasonType: 'arbete',
    landlordStatus: 'INCOMING',
    startDate: '2026-03-01',
    sentToHostAt: '2026-02-10',
    createdAt: '2026-02-01'
  },
  {
    id: 1220,
    address: 'Tegnérgatan 8',
    city: 'Stockholm',
    rent: 9600,
    rentTo: 10400,
    reasonType: 'sambo',
    landlordStatus: 'APPROVED',
    startDate: '2025-12-01',
    endDate: '2026-06-01',
    sentToHostAt: '2025-11-15',
    createdAt: '2025-11-01'
  },
  {
    id: 1221,
    address: 'Folkungagatan 72',
    city: 'Stockholm',
    rent: 7000,
    rentTo: 7200,
    reasonType: 'arbete',
    landlordStatus: 'CLOSED',
    startDate: '2024-08-01',
    endDate: '2025-08-01',
    sentToHostAt: '2024-07-10',
    createdAt: '2024-07-01'
  }
];

const MOCK_RECURRING = [
  {
    address: 'Odengatan 55',
    city: 'Stockholm',
    applicationCount: 2,
    totalMonths: 24,
    avgOverchargePercent: 6
  }
];

const MOCK_MONTHLY_FLOW = [
  { month: '2025-04', incoming: 5, approved: 3, rejected: 1 },
  { month: '2025-05', incoming: 8, approved: 5, rejected: 2 },
  { month: '2025-06', incoming: 6, approved: 4, rejected: 1 },
  { month: '2025-07', incoming: 9, approved: 6, rejected: 2 },
  { month: '2025-08', incoming: 11, approved: 7, rejected: 3 },
  { month: '2025-09', incoming: 7, approved: 5, rejected: 1 },
  { month: '2025-10', incoming: 8, approved: 6, rejected: 2 },
  { month: '2025-11', incoming: 10, approved: 7, rejected: 2 },
  { month: '2025-12', incoming: 4, approved: 3, rejected: 0 },
  { month: '2026-01', incoming: 7, approved: 4, rejected: 1 },
  { month: '2026-02', incoming: 9, approved: 6, rejected: 2 },
  { month: '2026-03', incoming: 6, approved: 4, rejected: 1 }
];

const REASON_LABELS = {
  arbete: 'Arbete',
  studie: 'Studier',
  sambo: 'Sambo',
  other: 'Övrigt'
};

const STATUS_LABELS = {
  INCOMING: 'Inkommande',
  READY: 'Redo',
  APPROVED: 'Aktiv',
  REJECTED: 'Avslagen',
  CLOSED: 'Avslutad',
  REMOVED: 'Borttagen',
  PENDING_REMOVE: 'Borttagning'
};

export default {
  components: { BaseLoadingSpinner },
  data() {
    return {
      isLoading: false,
      hasError: false,
      activeTab: 'risk',
      sortBy: 'risk',
      applications: [],
      recurringAddresses: [],
      monthlyFlow: [],
      activeCount: 0,
      tabs: [
        { id: 'risk', label: 'Riskflaggor' },
        { id: 'overcharge', label: 'Överhyra' },
        { id: 'recurring', label: 'Återkommande adresser' },
        { id: 'flow', label: 'Ärendeflöde' }
      ],
      sortOptions: [
        { id: 'risk', label: 'Riskscore' },
        { id: 'overcharge', label: 'Överhyra' },
        { id: 'duration', label: 'Avtalslängd' }
      ]
    };
  },
  computed: {
    overchargeApps() {
      return this.applications
        .filter(a => this.overchargePercent(a) > 15)
        .sort((a, b) => this.overchargePercent(b) - this.overchargePercent(a));
    },

    overchargeCount() {
      return this.overchargeApps.length;
    },

    overchargePercOfActive() {
      if (!this.activeCount) return 0;
      const activeOvercharge = this.overchargeApps.filter(
        a =>
          !['REMOVED', 'PENDING_REMOVE', 'CLOSED', 'REJECTED'].includes(
            a.landlordStatus
          )
      ).length;
      return Math.round((activeOvercharge / this.activeCount) * 100);
    },

    avgOverchargePercent() {
      if (!this.overchargeApps.length) return 0;
      const sum = this.overchargeApps.reduce(
        (s, a) => s + this.overchargePercent(a),
        0
      );
      return Math.round(sum / this.overchargeApps.length);
    },

    maxOverchargePercent() {
      if (!this.overchargeApps.length) return 0;
      return Math.max(
        ...this.overchargeApps.map(a => this.overchargePercent(a))
      );
    },

    maxOverchargeAddress() {
      if (!this.overchargeApps.length) return '';
      const top = this.overchargeApps[0];
      return top.address || '';
    },

    criticalCount() {
      return this.applications.filter(a => this.calcRiskScore(a) >= 70).length;
    },

    longestRecurring() {
      if (!this.recurringAddresses.length) return { months: 0, address: '' };
      const top = [...this.recurringAddresses].sort(
        (a, b) => (b.totalMonths || 0) - (a.totalMonths || 0)
      )[0];
      return { months: top.totalMonths || 0, address: top.address };
    },

    recurringWithOvercharge() {
      return this.recurringAddresses.filter(a => a.avgOverchargePercent > 0)
        .length;
    },

    sortedRiskApps() {
      const apps = this.applications.map(a => ({
        ...a,
        riskScore: this.calcRiskScore(a)
      }));

      return apps.sort((a, b) => {
        if (this.sortBy === 'risk') return b.riskScore - a.riskScore;
        if (this.sortBy === 'overcharge')
          return this.overchargePercent(b) - this.overchargePercent(a);
        if (this.sortBy === 'duration')
          return this.contractMonths(b) - this.contractMonths(a);
        return 0;
      });
    },

    avgProcessingDays() {
      const approved = this.applications.filter(
        a => a.landlordStatus === 'APPROVED' && a.sentToHostAt && a.startDate
      );
      if (!approved.length) return 0;
      const totalDays = approved.reduce((sum, a) => {
        const sent = new Date(a.sentToHostAt || a.createdAt);
        const start = new Date(a.startDate);
        return sum + Math.max(0, (start - sent) / (1000 * 60 * 60 * 24));
      }, 0);
      return Math.round(totalDays / approved.length);
    },

    topReason() {
      const counts = {};
      this.applications.forEach(a => {
        const r = a.reasonType || 'other';
        counts[r] = (counts[r] || 0) + 1;
      });
      const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      if (!top) return { label: '-', count: 0 };
      return {
        label: REASON_LABELS[top[0]] || top[0],
        count: top[1]
      };
    },

    approvalRate() {
      const decided = this.applications.filter(a =>
        ['APPROVED', 'REJECTED'].includes(a.landlordStatus)
      );
      if (!decided.length) return 0;
      const approved = decided.filter(
        a => a.landlordStatus === 'APPROVED'
      ).length;
      return Math.round((approved / decided.length) * 100);
    },

    maxMonthlyValue() {
      return Math.max(
        ...this.monthlyFlow.map(m =>
          Math.max(m.incoming, m.approved, m.rejected)
        ),
        1
      );
    }
  },
  watch: {
    activeTab(val) {
      if (val === 'overcharge') {
        this.$nextTick(() => this.drawScatter());
      }
    }
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      // TODO: Replace with real endpoint when available
      this.isLoading = true;
      setTimeout(() => {
        this.applications = MOCK_APPLICATIONS;
        this.recurringAddresses = MOCK_RECURRING;
        this.monthlyFlow = MOCK_MONTHLY_FLOW;
        this.activeCount = MOCK_APPLICATIONS.filter(
          a =>
            !['REMOVED', 'PENDING_REMOVE', 'CLOSED', 'REJECTED'].includes(
              a.landlordStatus
            )
        ).length;
        this.isLoading = false;
      }, 400);
    },

    drawScatter() {
      const container = this.$refs.scatterContainer;
      const svgEl = this.$refs.scatterSvg;
      if (!container || !svgEl) return;

      const apps = this.applications.filter(
        a => Number(a.rent) > 0 && Number(a.rentTo) > 0
      );
      if (!apps.length) return;

      // Clear previous
      d3.select(svgEl).selectAll('*').remove();

      const margin = { top: 20, right: 30, bottom: 50, left: 60 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 360 - margin.top - margin.bottom;

      const svg = d3
        .select(svgEl)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const allRents = apps.flatMap(a => [Number(a.rent), Number(a.rentTo)]);
      const minR = Math.floor(Math.min(...allRents) / 1000) * 1000;
      const maxR = Math.ceil(Math.max(...allRents) / 1000) * 1000;

      const x = d3.scaleLinear().domain([minR, maxR]).range([0, width]);
      const y = d3.scaleLinear().domain([minR, maxR]).range([height, 0]);

      // Grid lines
      svg
        .append('g')
        .attr('class', 'grid')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickSize(-height).tickFormat(''))
        .selectAll('line')
        .attr('stroke', '#e8e6de')
        .attr('stroke-dasharray', '3,3');

      svg
        .append('g')
        .attr('class', 'grid')
        .call(d3.axisLeft(y).tickSize(-width).tickFormat(''))
        .selectAll('line')
        .attr('stroke', '#e8e6de')
        .attr('stroke-dasharray', '3,3');

      // Remove domain lines from grids
      svg.selectAll('.grid .domain').remove();

      // Axes
      svg
        .append('g')
        .attr('transform', `translate(0,${height})`)
        .call(
          d3
            .axisBottom(x)
            .ticks(5)
            .tickFormat(d => d / 1000 + 'k')
        )
        .selectAll('text')
        .attr('fill', '#878b9d')
        .attr('font-size', '11px');

      svg
        .append('g')
        .call(
          d3
            .axisLeft(y)
            .ticks(5)
            .tickFormat(d => d / 1000 + 'k')
        )
        .selectAll('text')
        .attr('fill', '#878b9d')
        .attr('font-size', '11px');

      // Remove domain lines from axes
      svg.selectAll('.domain').attr('stroke', '#d6cebf');

      // Axis labels
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', height + 42)
        .attr('text-anchor', 'middle')
        .attr('fill', '#878b9d')
        .attr('font-size', '12px')
        .text('Förstahandshyra (kr/mån)');

      svg
        .append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -height / 2)
        .attr('y', -45)
        .attr('text-anchor', 'middle')
        .attr('fill', '#878b9d')
        .attr('font-size', '12px')
        .text('Andrahandshyra (kr/mån)');

      // Diagonal line (tillåten nivå = same rent)
      svg
        .append('line')
        .attr('x1', x(minR))
        .attr('y1', y(minR))
        .attr('x2', x(maxR))
        .attr('y2', y(maxR))
        .attr('stroke', '#2a9d8f')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,4')
        .attr('opacity', 0.6);

      svg
        .append('text')
        .attr('x', x(maxR) - 4)
        .attr('y', y(maxR) - 8)
        .attr('text-anchor', 'end')
        .attr('fill', '#2a9d8f')
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .text('Tillåten nivå');

      // Points
      const dotColor = pct => {
        if (pct <= 0) return '#2a9d8f';
        if (pct < 30) return '#c98a00';
        return '#e04545';
      };

      const dotRadius = pct => {
        if (pct <= 0) return 6;
        return Math.min(6 + pct * 0.3, 16);
      };

      svg
        .selectAll('circle.dot')
        .data(apps)
        .enter()
        .append('circle')
        .attr('class', 'dot')
        .attr('cx', d => x(Number(d.rent)))
        .attr('cy', d => y(Number(d.rentTo)))
        .attr('r', 0)
        .attr('fill', d => dotColor(this.overchargePercent(d)))
        .attr('opacity', 0.75)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
        .transition()
        .duration(600)
        .delay((d, i) => i * 50)
        .attr('r', d => dotRadius(this.overchargePercent(d)));
    },

    calcRiskScore(app) {
      let score = 0;
      // Overcharge
      const pct = this.overchargePercent(app);
      if (pct > 40) score += 40;
      else if (pct > 30) score += 30;
      else if (pct > 15) score += 15;

      // Long contract
      const months = this.contractMonths(app);
      if (months > 24) score += 25;
      else if (months > 12) score += 15;
      else if (months > 6) score += 5;

      // Recurring address
      const isRecurring = this.recurringAddresses.some(
        r => r.address === app.address && r.applicationCount >= 2
      );
      if (isRecurring) score += 20;

      // Unclear reason
      if (!app.reasonType || app.reasonType === 'other') score += 10;

      return Math.min(score, 100);
    },

    overchargePercent(app) {
      const rent = Number(app.rent);
      const rentTo = Number(app.rentTo);
      if (!rent || !rentTo || isNaN(rent) || isNaN(rentTo) || rent <= 0)
        return 0;
      return Math.round(((rentTo - rent) / rent) * 100);
    },

    contractMonths(app) {
      if (!app.startDate) return 0;
      const start = new Date(app.startDate);
      const end = app.endDate ? new Date(app.endDate) : new Date();
      return Math.round((end - start) / (1000 * 60 * 60 * 24 * 30));
    },

    extensionCount(app) {
      // Approximation: each 12-month period after the first is an extension
      const months = this.contractMonths(app);
      if (months <= 12) return 0;
      return Math.floor((months - 1) / 12);
    },

    getFlags(app) {
      const flags = [];
      const pct = this.overchargePercent(app);
      if (pct > 15) {
        flags.push({
          text: `Överhyra +${pct}%`,
          type: pct > 30 ? 'danger' : 'warning'
        });
      }

      const ext = this.extensionCount(app);
      if (ext > 0) {
        flags.push({
          text: `${ext} förlängning${ext > 1 ? 'ar' : ''}`,
          type: ext >= 3 ? 'danger' : 'warning'
        });
      }

      if (!app.reasonType || app.reasonType === 'other') {
        flags.push({ text: 'Oklart skäl', type: 'neutral' });
      }

      return flags;
    },

    riskLevel(score) {
      if (score >= 70) return { class: 'critical' };
      if (score >= 40) return { class: 'warning' };
      return { class: 'low' };
    },

    reasonLabel(type) {
      return REASON_LABELS[type] || type || 'Oklart';
    },

    statusLabel(status) {
      return STATUS_LABELS[status] || status || '\u2014';
    },

    statusClass(status) {
      const map = {
        APPROVED: 'approved',
        REJECTED: 'rejected',
        INCOMING: 'incoming',
        READY: 'ready',
        CLOSED: 'closed',
        REMOVED: 'removed'
      };
      return map[status] || '';
    },

    formatNum(n) {
      if (n == null || n === '') return '0';
      const num = Number(n);
      if (isNaN(num)) return '0';
      return num.toLocaleString('sv-SE');
    },

    monthLabel(ym) {
      if (!ym) return '';
      const [, m] = ym.split('-');
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'Maj',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Okt',
        'Nov',
        'Dec'
      ];
      return months[parseInt(m) - 1] || m;
    },

    barHeight(val) {
      if (!val) return 0;
      return Math.max(4, (val / this.maxMonthlyValue) * 120);
    }
  }
};
</script>

<style scoped>
.sublet-dash {
  background: #fff;
  border-radius: 12px;
  color: #2c3e50;
  overflow: hidden;
  min-height: 400px;
}

.example-data-banner {
  background: #fff8e1;
  border-bottom: 1px solid #f0e4b8;
  color: #8a6d00;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
  padding: 8px 16px;
}

.sublet-loading,
.sublet-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.sublet-tabs {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e8e6de;
  padding: 0 24px;
}

.sublet-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: #878b9d;
  padding: 12px 18px;
  font-size: 1.2rem;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
}

.sublet-tab.active {
  color: #2c3e50;
  border-bottom-color: #2c3e50;
  font-weight: 700;
}

.sublet-summary {
  margin-left: auto;
  display: flex;
  gap: 12px;
}

.summary-badge {
  font-size: 1.1rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
}

.summary-badge.critical {
  color: #e04545;
}

.summary-badge.overcharge {
  color: #c98a00;
}

.summary-badge.active-count {
  color: #2a9d8f;
}

.tab-content {
  padding: 24px;
}

/* Stat cards */
.stat-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #f8f7f7;
  border-radius: 10px;
  border: 1px solid #e8e6de;
  padding: 18px;
}

.stat-card-label {
  font-size: 1rem;
  color: #878b9d;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
}

.stat-card-value {
  font-size: 2.2rem;
  font-weight: 800;
  color: #2c3e50;
}

.stat-card-value.warning {
  color: #c98a00;
}

.stat-card-value.danger {
  color: #e04545;
}

.stat-card-sub {
  font-size: 1.1rem;
  color: #878b9d;
  margin-top: 2px;
}

/* Sort bar */
.sort-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.sort-label {
  font-size: 1.1rem;
  color: #878b9d;
}

.sort-btn {
  background: #f8f7f7;
  border: 1px solid #e8e6de;
  border-radius: 6px;
  color: #878b9d;
  padding: 4px 12px;
  font-size: 1.1rem;
  cursor: pointer;
  font-family: inherit;
}

.sort-btn.active {
  background: #2a9d8f;
  color: #fff;
  border-color: #2a9d8f;
  font-weight: 700;
}

/* Risk table */
.risk-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 1.15rem;
}

.risk-table th {
  text-align: left;
  padding: 10px 12px;
  color: #878b9d;
  font-weight: 600;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e8e6de;
}

.risk-table td {
  padding: 12px;
  border-bottom: 1px solid #eeedea;
  color: #5a6370;
}

.id-cell {
  color: #2a9d8f;
  font-weight: 600;
}

.risk-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
}

.risk-badge.critical {
  background: #fde8e8;
  color: #e04545;
}

.risk-badge.warning {
  background: #fef3d6;
  color: #c98a00;
}

.risk-badge.low {
  background: #e0f5f2;
  color: #2a9d8f;
}

.overcharge-pct {
  font-weight: 700;
  margin-left: 4px;
}

.overcharge-pct.high {
  color: #e04545;
}

.overcharge-pct.medium {
  color: #c98a00;
}

.ext-note {
  color: #878b9d;
  font-size: 1rem;
}

/* Flags */
.flags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.flag-badge {
  font-size: 1rem;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
  white-space: nowrap;
}

.flag-badge.danger {
  background: #fde8e8;
  color: #e04545;
}

.flag-badge.warning {
  background: #fef3d6;
  color: #c98a00;
}

.flag-badge.info {
  background: #e0f5f2;
  color: #2a9d8f;
}

.flag-badge.neutral {
  background: #f0f0ee;
  color: #6b7280;
}

/* Status badge */
.status-badge {
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-badge.approved {
  background: #e0f5f2;
  color: #2a9d8f;
}

.status-badge.rejected {
  background: #fde8e8;
  color: #e04545;
}

.status-badge.incoming,
.status-badge.ready {
  background: #fef3d6;
  color: #c98a00;
}

.status-badge.closed,
.status-badge.removed {
  background: #f0f0ee;
  color: #878b9d;
}

/* Section heading */
.section-heading {
  font-size: 1.1rem;
  font-weight: 700;
  color: #878b9d;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 16px 0;
}

/* Recurring addresses */
.recurring-row {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 14px 16px;
  background: #f8f7f7;
  border-radius: 8px;
  margin-bottom: 8px;
}

.recurring-address {
  flex: 1;
  min-width: 180px;
}

.recurring-address strong {
  display: block;
  color: #2c3e50;
  font-size: 1.2rem;
}

.recurring-city {
  font-size: 1.1rem;
  color: #878b9d;
}

.recurring-stat {
  text-align: center;
  min-width: 100px;
}

.recurring-stat strong {
  display: block;
  font-size: 1.4rem;
  color: #2a9d8f;
}

.recurring-stat span {
  font-size: 1rem;
  color: #878b9d;
}

.text-danger {
  color: #e04545 !important;
}

.text-warning {
  color: #c98a00 !important;
}

/* Flow chart */
.flow-chart-section {
  background: #f8f7f7;
  border-radius: 10px;
  border: 1px solid #e8e6de;
  padding: 20px;
  margin-bottom: 24px;
}

.flow-chart {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 160px;
  padding-top: 20px;
}

.flow-bar-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.flow-bars {
  display: flex;
  gap: 3px;
  align-items: flex-end;
  height: 140px;
}

.flow-bar {
  width: 14px;
  border-radius: 3px 3px 0 0;
  position: relative;
  min-height: 0;
  transition: height 0.3s ease;
}

.flow-bar.incoming {
  background: #4e8cff;
}

.flow-bar.approved {
  background: #2a9d8f;
}

.flow-bar.rejected {
  background: #ff6b6b;
}

.bar-val {
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.9rem;
  color: #878b9d;
  white-space: nowrap;
}

.flow-month-label {
  font-size: 1rem;
  color: #878b9d;
  margin-top: 6px;
}

.flow-legend {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 12px;
}

.flow-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.1rem;
  color: #878b9d;
}

.flow-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.flow-legend-dot.incoming {
  background: #4e8cff;
}

.flow-legend-dot.approved {
  background: #2a9d8f;
}

.flow-legend-dot.rejected {
  background: #ff6b6b;
}

/* Scatter chart */
.scatter-section {
  margin-top: 24px;
  background: #f8f7f7;
  border-radius: 10px;
  border: 1px solid #e8e6de;
  padding: 20px;
}

.scatter-subtitle {
  font-size: 1.1rem;
  color: #878b9d;
  margin: 0 0 16px 0;
}

.scatter-container {
  width: 100%;
  min-height: 360px;
}

.scatter-container svg {
  display: block;
}

.scatter-legend {
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 12px;
}

.scatter-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.1rem;
  color: #5a6370;
}

.scatter-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
</style>
